import { useState } from 'react'
import { Box } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface EntityIconProps {
    /** Pre-built icon URL — callers build it (buildIconPath / buildNodeIconPath / buildServiceImagePath). */
    src?: string | null
    alt?: string
    /** Size + layout classes for the inline icon, e.g. "h-7 w-7 shrink-0". `object-contain` is applied. */
    className?: string
    /** Pixel size of the hover-enlarged icon. */
    enlargeSize?: number
    /** Skip the hover-enlarge (e.g. inside an already-zoomable canvas node). */
    noEnlarge?: boolean
    /** Extra classes for the inline icon's fallback box. */
    fallbackClassName?: string
    /**
     * The image's own pixel size, once it has loaded. For a screen that judges RESOLUTION — the
     * graphics library asks whether a file has the pixels to stay sharp when the canvas zooms — and
     * costs nothing, because the image is on screen anyway. Not fired for a broken src.
     */
    onLoadedSize?: (size: { width: number; height: number }) => void
}

/**
 * Shared object/device icon. The single place icons are rendered across the app: it shows the
 * icon image (with a Box fallback for a missing/broken src) and, on hover, an enlarged copy in
 * a tooltip so small list/table/card icons can be read clearly. Because every icon flows
 * through here, the hover behaviour (and any future change) lives in one component.
 */
export function EntityIcon({
    src,
    alt = '',
    className,
    enlargeSize = 96,
    noEnlarge = false,
    fallbackClassName,
    onLoadedSize,
}: EntityIconProps) {
    const [err, setErr] = useState(false)
    const ok = !!src && !err

    if (!ok) {
        return <Box className={cn('text-muted-foreground', className, fallbackClassName)} aria-hidden />
    }

    const img = (
        <img
            src={src!}
            alt={alt}
            className={cn('object-contain', className)}
            draggable={false}
            onError={() => setErr(true)}
            onLoad={onLoadedSize
                ? (e) => {
                    const img = e.currentTarget
                    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                        onLoadedSize({ width: img.naturalWidth, height: img.naturalHeight })
                    }
                }
                : undefined}
        />
    )

    if (noEnlarge) return img

    // The trigger is a <span>, not the bare <img>: Radix asChild needs a reliable, non-draggable
    // element to attach pointer handlers to, and a span receives hover even inside a clickable
    // table row (the row's onClick only bows out for button/a/[role=button], not for an <img>).
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex shrink-0">{img}</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="p-2">
                    <img
                        src={src!}
                        alt={alt}
                        className="object-contain"
                        style={{ width: enlargeSize, height: enlargeSize }}
                        draggable={false}
                    />
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
